/**
 * index.tsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

import * as React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import { Provider } from 'react-redux';

// Use consistent styling
import 'sanitize.css/sanitize.css';

// Import root app
import { App } from 'app';

import { HelmetProvider } from 'react-helmet-async';

import { configureAppStore } from 'store/configureStore';

import reportWebVitals from 'reportWebVitals';

// Initialize languages
import './locales/i18n';

// Init react-toastify
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Init Swiper
import SwiperCore, {
  Pagination,
  Navigation,
  EffectCoverflow,
  Autoplay,
} from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import { initTracking } from 'hooks/useTracking/useTracking';
import AuthenticatedUserProvider from 'hooks/useAuthenticatedUser/useAuthenticatedUser';

SwiperCore.use([Pagination, Navigation, EffectCoverflow, Autoplay]);

const store = configureAppStore();
const MOUNT_NODE = document.getElementById('root') as HTMLElement;
initTracking();

ReactDOMClient.createRoot(MOUNT_NODE!).render(
  <Provider store={store}>
    <AuthenticatedUserProvider>
      <HelmetProvider>
        <React.StrictMode>
          <App />
          <ToastContainer
            position="bottom-left"
            autoClose={3200}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            draggable
            pauseOnHover
          />
        </React.StrictMode>
      </HelmetProvider>
    </AuthenticatedUserProvider>
  </Provider>,
);

// Hot reloadable translation json files
if (module.hot) {
  module.hot.accept(['./locales/i18n'], () => {
    // No need to render the App again because i18next works with the hooks
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
